/**
 * Verifier Welcome Page
 * Shown to new Verifiers after email verification / onboarding
 * Guides them to complete profile, find verification work, and understand earnings
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Person,
  VerifiedUser,
  ArrowForward,
  Explore
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { brand, text, bg, border, shadow, easing } from '../theme/colors';

const VerifierWelcomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const firstName = user?.profile?.firstName || user?.first_name || 'there';
  const profileComplete = user?.profile?.onboardingComplete || user?.profile?.onboarding_complete;

  const markWelcomeSeen = async () => {
    try {
      await apiClient.put('/auth/users/profile', { welcome_seen: true });
    } catch {
      // Non-fatal
    }
  };

  const handleCompleteProfile = async () => {
    await markWelcomeSeen();
    navigate('/verifier/profile');
  };

  const handleFindWork = async () => {
    await markWelcomeSeen();
    navigate('/verifier/available-verifications');
  };

  const handleExplore = async () => {
    await markWelcomeSeen();
    navigate('/dashboard');
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: `1px solid ${border.light}`,
          boxShadow: shadow.md,
          borderRadius: 3,
        }}
      >
        {/* Welcome header */}
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: `${brand.accent}14`,
              mb: 2,
            }}
          >
            <VerifiedUser sx={{ fontSize: 32, color: brand.accent }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: text.primary }} gutterBottom>
            Welcome, {firstName}!
          </Typography>
          <Typography variant="body1" sx={{ color: text.secondary }}>
            Your verifier account is ready. Here's how to start earning.
          </Typography>
        </Box>

        {/* How it works */}
        <Box sx={{ mb: 4, p: 3, bgcolor: bg.subtle, borderRadius: 2, border: `1px solid ${border.light}` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: text.primary, mb: 2, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
            How It Works for Verifiers
          </Typography>
          {[
            { num: '1', label: 'Complete Your Profile', desc: 'Add your qualifications and location to get matched with nearby jobs' },
            { num: '2', label: 'Accept Verification Jobs', desc: 'Browse available inspections and accept ones near you' },
            { num: '3', label: 'Inspect & Earn', desc: 'Visit sites, submit photo evidence + AI reports, and get paid per verification' },
          ].map((step, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: i < 2 ? 2 : 0 }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%',
                bgcolor: brand.accent, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, mt: 0.25,
              }}>
                {step.num}
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" sx={{ color: text.secondary }}>
                  {step.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>


        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textAlign: 'center', color: text.primary }}>
          What would you like to do first?
        </Typography>

        {/* Primary Action - Complete Profile (if incomplete) or Find Work */}
        {!profileComplete ? (
          <Card
            sx={{
              mb: 2,
              cursor: 'pointer',
              border: `2px solid ${brand.accent}`,
              boxShadow: shadow.sm,
              transition: `transform 0.2s ${easing.smooth}, box-shadow 0.2s ${easing.smooth}`,
              '&:hover': {
                bgcolor: `${brand.accent}06`,
                transform: 'translateY(-2px)',
                boxShadow: shadow.md,
              },
              '&:active': { transform: 'translateY(0)' },
            }}
            onClick={handleCompleteProfile}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                bgcolor: brand.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Person sx={{ color: 'white' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: text.primary }}>
                  Complete Your Profile
                </Typography>
                <Typography variant="body2" sx={{ color: text.secondary }}>
                  Add your service areas, experience, ID document, and professional bio
                </Typography>
              </Box>
              <ArrowForward sx={{ color: brand.accent }} />
            </CardContent>
          </Card>
        ) : (
          <Card
            sx={{
              mb: 2,
              cursor: 'pointer',
              border: `2px solid ${brand.accent}`,
              boxShadow: shadow.sm,
              transition: `transform 0.2s ${easing.smooth}, box-shadow 0.2s ${easing.smooth}`,
              '&:hover': {
                bgcolor: `${brand.accent}06`,
                transform: 'translateY(-2px)',
                boxShadow: shadow.md,
              },
              '&:active': { transform: 'translateY(0)' },
            }}
            onClick={handleFindWork}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                bgcolor: brand.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <VerifiedUser sx={{ color: 'white' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: text.primary }}>
                  Find Verification Work
                </Typography>
                <Typography variant="body2" sx={{ color: text.secondary }}>
                  Browse nearby construction verifications that need your expertise
                </Typography>
              </Box>
              <ArrowForward sx={{ color: brand.accent }} />
            </CardContent>
          </Card>
        )}

        {/* Secondary Action */}
        <Card
          sx={{
            mb: 2,
            cursor: 'pointer',
            border: `1px solid ${border.light}`,
            transition: `transform 0.2s ${easing.smooth}, border-color 0.2s ${easing.smooth}`,
            '&:hover': {
              borderColor: brand.accent,
              bgcolor: `${brand.accent}06`,
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
          }}
          onClick={!profileComplete ? handleFindWork : handleCompleteProfile}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: `${brand.accent}12`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {!profileComplete
                ? <VerifiedUser sx={{ color: brand.accent }} />
                : <Person sx={{ color: brand.accent }} />
              }
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: text.primary }}>
                {!profileComplete ? 'Browse Work First' : 'Update Your Profile'}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                {!profileComplete
                  ? 'See what verifications are available before completing your profile'
                  : 'Add certifications, portfolio work, and qualifications to get matched with more jobs'
                }
              </Typography>
            </Box>
            <ArrowForward sx={{ color: text.tertiary }} />
          </CardContent>
        </Card>

        {/* Tertiary Action - Go to Dashboard */}
        <Card
          sx={{
            cursor: 'pointer',
            border: `1px solid ${border.light}`,
            transition: `border-color 0.2s ${easing.smooth}`,
            '&:hover': {
              borderColor: border.default,
              bgcolor: bg.subtle,
            },
          }}
          onClick={handleExplore}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: bg.subtle,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Explore sx={{ color: text.tertiary }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: text.primary }}>
                Go to Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                Explore the platform at your own pace
              </Typography>
            </Box>
            <ArrowForward sx={{ color: text.disabled }} />
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Typography variant="caption" sx={{ color: text.tertiary }} textAlign="center" display="block">
          You can always find verification work from the marketplace in your dashboard
        </Typography>
      </Paper>
    </Container>
  );
};

export default VerifierWelcomePage;
